import React, { useEffect, useState, useCallback, memo } from 'react';
import moment from 'moment-timezone';
import classNames from 'classnames';
import { Button, Modal } from 'react-bootstrap';
import useSearchDebounce from '../../../../hooks/useSearchDebounce';
import xhr from '../../../../utils/xhr';
import ModalShiftDetails from '../ModalShiftDetails';
import SearchProfessional from './SearchProfessional';
import LoadingSpinner from '../../../spinner/LoadingSpinner';
import SelectedProfessional from './SelectedProfessional';
import AvailableProfessionals from './AvailableProfessionals';
import AVAILABILITY_BADGES from '../../../../constants/AvailabilityBadges';
import ConfirmAssignCaregiverModal from '../ConfirmAssignCaregiverModal';
import { SEARCH_MIN_CHARS } from '../../../../constants';
import { SHIFT_TYPES } from '../../../../constants/ShiftStatus';

const buildShiftKey = (shift) =>
	`${shift.formatted_start_time}-${shift.facility_name}-${shift.rank}-${shift.shift_structure_id}`;

const getOpenSlotsCount = (shiftsForAllDay, currentShift, isGlobalCalendar) => {
	if (isGlobalCalendar) {
		const key = buildShiftKey(currentShift);
		const shiftGroup = shiftsForAllDay[key];

		if (!shiftGroup) {
			return 0;
		}

		return (shiftGroup.unfilledSlots ?? []).filter(
			({ slot, shift }) =>
				!slot.caregiver_id &&
				shift.shift_structure_id === currentShift.shift_structure_id &&
				(shift.shift_type === SHIFT_TYPES.FLEX || !!slot.is_hybrid)
		).length;
	}

	return shiftsForAllDay
		.flatMap((shift) => shift.slots ?? [])
		.filter(
			(slot) =>
				!slot.caregiver_id &&
				currentShift.shift_structure_id === slot.shift_structure_id &&
				(!!slot.is_flex || !!slot.is_hybrid)
		).length;
};

const AssignCaregiverModal = ({ onCancel, onAssign, shift, isGlobalCalendar }) => {
	const [searchValue, setSearchValue] = useState('');
	const [selectedCaregivers, setSelectedCaregivers] = useState([]);
	const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
	const [showConfirmationModal, setShowConfirmationModal] = useState(false);
	const [searchLoading, setSearchLoading] = useState(false);
	const [searchCaregivers, setSearchCaregivers] = useState([]);
	const [searchError, setSearchError] = useState(false);
	const [availableProfessionals, setAvailableProfessionals] = useState([]);
	const { end_time: endTime, start_time: startTime, resourceType, shiftsForAllDay } = shift;
	const date = moment.utc(!isGlobalCalendar ? shift.raw_start_time : shift.start_time).format('dddd, MMMM D, YYYY');
	const openSlots = getOpenSlotsCount(shiftsForAllDay, shift, isGlobalCalendar);
	const shiftId = isGlobalCalendar ? shift.id : shift.shift_id;
	const searchUrl = shift.is_hybrid ? '/hybrid/availability/find' : '/flex/availability/find';
	const addSelectedCaregiver = (caregiver) => {
		if (caregiver.status !== AVAILABILITY_BADGES.ASSIGNED.status) {
			setSearchValue('');
			setSearchCaregivers([]);
			const found = selectedCaregivers.find((c) => c.caregiver_id === caregiver.caregiver_id);
			if (!found) {
				setSelectedCaregivers([...selectedCaregivers, ...[caregiver]]);
			}
		}
	};

	const handleResults = (results = []) => setSearchCaregivers(results);

	const searchParams = shift.is_hybrid ? { shiftId } : { flexShiftId: shiftId };

	const { searchTermChange } = useSearchDebounce(
		searchUrl,
		searchValue,
		searchParams,
		setSearchValue,
		setSearchLoading,
		setSearchError,
		handleResults,
		SEARCH_MIN_CHARS
	);

	useEffect(() => {
		if (!searchValue && searchCaregivers.length > 0) {
			setSearchCaregivers([]);
		}
	}, [searchValue, searchCaregivers]);

	const assignCaregiver = async () => {
		setShowLoadingSpinner(true);
		const [caregiver] = selectedCaregivers;
		const assignUrl = shift.is_hybrid ? '/hybrid/caregivers/assign' : '/flex/caregivers/assign';

		const payload = {
			caregiverId: caregiver.caregiver_id,
			slotId: shift.slot_id,
			oldUnitId: shift.unit_id || null,
			...(shift.is_hybrid ? { shiftId } : { flexShiftId: shiftId })
		};
		try {
			await xhr.request('POST', assignUrl, payload);
			onAssign('success', 'Professional successfully assigned to shift');
		} catch (error) {
			const isValidationError = error.response?.data?.validationError;
			let message = 'There was a problem assigning the professional to the shift';
			if (isValidationError) {
				const [validationError] = error.response.data.errors;
				message = validationError.message;
			}
			onAssign('error', message);
		} finally {
			setShowLoadingSpinner(false);
		}
	};

	const handleAssignCaregiver = async () => {
		const [caregiver] = selectedCaregivers;

		if (caregiver.status === AVAILABILITY_BADGES.UNAVAILABLE.status) {
			setShowConfirmationModal(true);
			return;
		}
		await assignCaregiver();
	};

	const removeSelectedProfessional = (e, caregiver) => {
		e.preventDefault();
		const newSelectedCaregiver = selectedCaregivers.filter((c) => c !== caregiver);
		setSelectedCaregivers(newSelectedCaregiver);
	};

	const getFlexCaregivers = useCallback(async () => {
		setShowLoadingSpinner(true);
		try {
			const url = shift.is_hybrid ? `${searchUrl}?shiftId=${shiftId}` : `${searchUrl}?flexShiftId=${shiftId}`;
			const { data } = await xhr.request('GET', url);
			setAvailableProfessionals(data);
		} finally {
			setShowLoadingSpinner(false);
		}
	}, [shiftId, setShowLoadingSpinner]);

	useEffect(() => {
		getFlexCaregivers();
	}, [getFlexCaregivers]);

	return (
		<Modal
			size="xl"
			show
			className={classNames('assign-caregiver-modal', 'no-gutters')}
			onHide={onCancel}
			backdropClassName="assign-caregiver-backdrop"
			scrollable
		>
			<Modal.Header closeButton>
				<Modal.Title id="assign-caregiver-modal-title">
					<b>Assign Flex {resourceType}</b> - <span>{date}</span>
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<ModalShiftDetails
					dateTime={date}
					resourceType={resourceType}
					startTime={isGlobalCalendar ? shift.formatted_start_time : startTime}
					endTime={isGlobalCalendar ? shift.formatted_end_time : endTime}
					openShift={openSlots}
					facilityName={shift.facility_name}
					isCaregiverInvite={false}
					unitName={shift.unit_name}
				/>
				<br />
				<br />
				<SearchProfessional
					addSelectedCaregiver={addSelectedCaregiver}
					errorSearch={searchError}
					searchDisabled={selectedCaregivers.length > 0}
					searchValue={searchValue}
					searchTermChange={searchTermChange}
					loading={searchLoading}
					searchedCaregivers={searchCaregivers}
					searchSectionTitle="Search and Select Professional"
					copy="Search for an internal staff professional with this license type to assign to this shift."
				/>
				<AvailableProfessionals
					professionals={availableProfessionals}
					addSelectedProfessional={addSelectedCaregiver}
					selectedProfessionals={selectedCaregivers}
				/>
				<SelectedProfessional
					selectedCaregivers={selectedCaregivers}
					removeSelectedProfessional={removeSelectedProfessional}
					selectedSectionTitle="Selected Professional"
				/>
				{showLoadingSpinner && <LoadingSpinner />}
			</Modal.Body>
			<Modal.Footer>
				<Button className="btn-purple" onClick={onCancel} variant="secondary" disabled={showLoadingSpinner}>
					Cancel
				</Button>
				<Button
					onClick={handleAssignCaregiver}
					id="send-invite-button"
					variant="primary"
					disabled={selectedCaregivers.length === 0 || showLoadingSpinner}
					className="no-gutters no-rounded"
				>
					Assign Shift
				</Button>
			</Modal.Footer>
			{showConfirmationModal && (
				<ConfirmAssignCaregiverModal
					onCancel={() => setShowConfirmationModal(false)}
					setShowModal={showConfirmationModal}
					onConfirm={() => assignCaregiver()}
				/>
			)}
		</Modal>
	);
};

export default memo(AssignCaregiverModal);
